import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { NO_AUTOFILL } from '../../utils/noAutofill';
import {
  CENTRAL_ACCOUNT_ROLES,
  OPERATOR_ACCOUNT_ROLES,
  normalizeEmail,
  roleLabel,
  validateAccountEditForm
} from '../../utils/portalAccounts';
import { phoneRecognitionHint } from '../../utils/phoneNumber';

/**
 * Stammdaten eines Portalzugangs ändern.
 *
 * Bisher waren angelegte Zugänge unveränderlich: ein Tippfehler im Namen, eine
 * nachzutragende Telefonnummer oder ein Rollenwechsel gingen nur direkt in
 * Firestore. Die E-Mail wird bewusst mitgeführt — sie ist der Benutzername, und
 * die Cloud Function ändert sie in Auth und Firestore gemeinsam.
 */
const PortalAccountEditDialog = ({
  open,
  account,
  isCentralAccount = false,
  isSaving = false,
  error = null,
  onSave,
  onClose
}) => {
  const [form, setForm] = useState({ name: '', phone: '', aiAlternatePhone: '', email: '', role: '' });
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open || !account) return;
    setForm({
      name: account.name || '',
      phone: account.phone || '',
      aiAlternatePhone: account.aiAlternatePhone || '',
      email: account.email || '',
      role: account.role || ''
    });
    setTouched({});
  }, [open, account]);

  const { valid, errors } = validateAccountEditForm(form);
  const showError = (field) => Boolean(touched[field] && errors[field]);
  const setField = (field) => (event) => setForm(prev => ({ ...prev, [field]: event.target.value }));
  const markTouched = (field) => () => setTouched(prev => ({ ...prev, [field]: true }));

  // Die Rolle bleibt in ihrer Welt: ein Unternehmer-Zugang wird kein
  // Zentral-Konto und umgekehrt — sonst liefe die Mandantentrennung leer.
  // Altrollen (central_admin/central_dispatch) bleiben als aktueller Wert
  // wählbar, damit die Auswahl bei Bestandskonten nicht leer steht.
  const assignableRoles = isCentralAccount ? CENTRAL_ACCOUNT_ROLES : OPERATOR_ACCOUNT_ROLES;
  const roleOptions = assignableRoles.includes(account?.role)
    ? assignableRoles
    : [account?.role, ...assignableRoles].filter(Boolean);
  const emailChanged = normalizeEmail(form.email) !== normalizeEmail(account?.email);

  const handleSave = () => {
    onSave({
      name: form.name,
      phone: form.phone,
      aiAlternatePhone: form.aiAlternatePhone,
      email: form.email,
      // Nur mitschicken, wenn wirklich gewechselt wurde: eine unveränderte
      // Altrolle würde die Server-Prüfung sonst als ungültig zurückweisen.
      ...(form.role && form.role !== account?.role ? { role: form.role } : {})
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Zugang bearbeiten</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            required
            fullWidth
            label="Name"
            value={form.name}
            onChange={setField('name')}
            onBlur={markTouched('name')}
            error={showError('name')}
            helperText={showError('name') ? errors.name : ' '}
            autoComplete={NO_AUTOFILL}
          />
          <TextField
            required
            fullWidth
            type="email"
            label="E-Mail"
            value={form.email}
            onChange={setField('email')}
            onBlur={markTouched('email')}
            error={showError('email')}
            helperText={showError('email') ? errors.email : 'Zugleich der Benutzername für die Anmeldung.'}
            autoComplete={NO_AUTOFILL}
          />
          {emailChanged && !errors.email && (
            <Alert severity="warning">
              Die Anmeldung erfolgt danach nur noch mit der neuen Adresse. Bitte
              dem Zugang mitteilen — das Passwort bleibt unverändert.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Telefon"
            value={form.phone}
            onChange={setField('phone')}
            error={phoneRecognitionHint(form.phone).error}
            helperText={phoneRecognitionHint(form.phone).text || 'Ruft der Zugang von dieser Nummer an, erkennt ihn der KI-Telefonist.'}
            autoComplete={NO_AUTOFILL}
          />
          <TextField
            fullWidth
            label="Weitere Nummer (optional)"
            value={form.aiAlternatePhone}
            onChange={setField('aiAlternatePhone')}
            error={phoneRecognitionHint(form.aiAlternatePhone).error}
            helperText={phoneRecognitionHint(form.aiAlternatePhone).text || 'Zweite Nummer für die Erkennung, z. B. privates Handy.'}
            autoComplete={NO_AUTOFILL}
          />
          <FormControl fullWidth>
            <InputLabel id="edit-account-role-label">Rolle</InputLabel>
            <Select
              labelId="edit-account-role-label"
              label="Rolle"
              value={form.role}
              onChange={setField('role')}
            >
              {roleOptions.map(role => (
                <MenuItem key={role} value={role}>{roleLabel(role)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" disabled={isSaving || !valid} onClick={handleSave}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortalAccountEditDialog;
